import React from "react";
import { Lightning, Warning } from "@phosphor-icons/react";
import { Card, Empty, Pill, aud, num } from "../ui";
import { AgreementPill, DECISIONS, DecisionPill, fmtDate, outcomeLabel } from "./parts";

const TABS = [["shadow", "New & open claims"], ["calibration", "Past-claims test"]];

function Filters({ decision, setDecision }) {
  const chips = [["", "All"], ...Object.entries(DECISIONS).map(([k, d]) => [k, d.label]), ["auto", "Would auto-approve"]];
  return (
    <div className="flex flex-wrap gap-1.5 mb-3" data-testid="wi-assess-filters">
      {chips.map(([k, l]) => (
        <button key={k || "all"} onClick={() => setDecision(k)} data-testid={`wi-assess-filter-${k || "all"}`}
          className={`text-[0.7rem] rounded-full px-2.5 py-1 border transition-colors ${decision === k ? "border-[#73bf44] text-white bg-[#73bf44]/10" : "border-neutral-800 text-neutral-400 hover:text-white"}`}>{l}</button>
      ))}
    </div>
  );
}

function Row({ r, onOpen }) {
  return (
    <tr onClick={() => onOpen(r)} className="border-b border-neutral-900 hover:bg-white/[0.03] cursor-pointer align-top" data-testid="wi-assess-row">
      <td className="py-2.5 pr-3 whitespace-nowrap"><div className={r.otma ? "font-mono text-neutral-100" : "text-neutral-500 italic"}>{r.otma || "No OTMA"}</div><div className="text-neutral-600">{r.chassis} · {r.claim_date}</div></td>
      <td className="py-2.5 pr-3 text-neutral-400">{r.model}<div className="text-neutral-600">{r.dealer}</div></td>
      <td className="py-2.5 pr-3 text-right tabular-nums text-[#d9b45b]">{aud(r.claimed_inc_gst, 2)}</td>
      <td className="py-2.5 pr-3 text-right tabular-nums font-heading font-bold text-white">{r.status === "done" ? r.confidence : "—"}</td>
      <td className="py-2.5 pr-3">
        {r.status === "running" ? <Pill tone="sky">Assessing…</Pill> : r.status === "failed" ? <Pill tone="red">Failed</Pill> : <DecisionPill decision={r.decision} />}
        <div className="flex gap-1 mt-1 flex-wrap">
          {r.auto_eligible && <Pill tone="green"><Lightning size={10} weight="fill" /> auto-eligible</Pill>}
          {r.blockers > 0 && <Pill tone="amber"><Warning size={10} /> {r.blockers} check{r.blockers > 1 ? "s" : ""}</Pill>}
        </div>
      </td>
      <td className="py-2.5 pr-3 text-neutral-300">{outcomeLabel(r.human_outcome)}<div className="mt-1"><AgreementPill value={r.agreement} /></div></td>
      <td className="py-2.5 text-neutral-600 whitespace-nowrap">{fmtDate(r.assessed_at)}</td>
    </tr>
  );
}

export function AssessmentTable({ mode, setMode, decision, setDecision, data, page, setPage, onOpen }) {
  return (
    <Card title={`Assessments${data ? ` · ${num(data.total)}` : ""}`} testId="wi-assess-table" actions={
      <div className="flex bg-black/40 border border-neutral-800 rounded-full p-0.5">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setMode(k)} data-testid={`wi-assess-tab-${k}`}
            className={`text-xs px-3 py-1.5 rounded-full transition-colors ${mode === k ? "bg-[#73bf44] text-[#050505] font-semibold" : "text-neutral-400 hover:text-white"}`}>{l}</button>
        ))}
      </div>
    }>
      <Filters decision={decision} setDecision={setDecision} />
      {!data ? <div className="h-40 animate-pulse" /> : !data.rows.length ? (
        <Empty>{mode === "shadow" ? "No new or open claims assessed yet. Switch on AI checks above once the historical database is validated." : "No past-claims test yet. Run one from the panel on the right."}</Empty>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-[0.6rem] uppercase tracking-wider text-neutral-500 text-left">
                <tr className="border-b border-neutral-800"><th className="py-2 pr-3">Claim</th><th className="py-2 pr-3">Model / dealer</th><th className="py-2 pr-3 text-right">Claimed</th>
                  <th className="py-2 pr-3 text-right">Confidence</th><th className="py-2 pr-3">AI suggestion</th><th className="py-2 pr-3">Human decision</th><th className="py-2">Assessed</th></tr>
              </thead>
              <tbody>{data.rows.map((r) => <Row key={`${r.claim_id}-${r.mode}`} r={r} onOpen={onOpen} />)}</tbody>
            </table>
          </div>
          <div className="flex items-center justify-end gap-3 pt-3 text-xs text-neutral-500">
            <button disabled={page === 0} onClick={() => setPage(page - 1)} className="hover:text-white disabled:opacity-30" data-testid="wi-assess-prev">Previous</button>
            <span>Page {page + 1} of {Math.max(1, Math.ceil(data.total / data.page_size))}</span>
            <button disabled={(page + 1) * data.page_size >= data.total} onClick={() => setPage(page + 1)} className="hover:text-white disabled:opacity-30" data-testid="wi-assess-next">Next</button>
          </div>
        </>
      )}
    </Card>
  );
}
